import { fabric } from "fabric";
import { iconCheck } from "../../../constants/Icons";
import { DirectionEnum, ObjectTypesEnum } from "../../../enums";
import { SeatFactory } from "../factories/seat.factory";
import { RowLabelHelper } from "./RowLabelHelper";
import { RowSeatLabelHelper } from "./RowSeatLabelHelper";
import { RowSeatLayoutHelper, CURVE_MODE_RELATIVE } from "./RowSeatLayoutHelper";

export class RowSeatHelper {
  static async updateSeatsQuantity({
    selectedItem,
    value,
    rowSeatSize,
    textRight,
    textLeft,
    seats,
    setSeatValue,
    refreshLabels = true,
    editable,
    container,
    objectOption,
    labelOffset,
  }: any) {
    selectedItem.set({
      seatsQuantity: value
    });
    if (!value) {
      const objectsToRemove = seats.slice(1);

      objectsToRemove.forEach((object: any) => {
        selectedItem.removeWithUpdate(object);
      });

      return;
    }

    const baselineDirection = RowSeatLayoutHelper.getRowDirection(seats);

    if (value > seats.length) {
      await this.addMissingSeats({
        selectedItem,
        value,
        seats,
        rowSeatSize,
        editable,
        container,
        objectOption,
      });
    } else {
      this.removeExtraSeats({
        selectedItem,
        value,
        seats,
      });
    }

    const seatsUpdated = selectedItem.getObjects(ObjectTypesEnum.Group);

    this.applyDistribution({
      selectedItem,
      seats: seatsUpdated,
      setSeatValue,
    });

    const direction = baselineDirection
      ? {
          first: seatsUpdated[0],
          firstCenter: seatsUpdated[0].getCenterPoint(),
          ux: baselineDirection.ux,
          uy: baselineDirection.uy,
          px: baselineDirection.px,
          py: baselineDirection.py,
        }
      : null;

    RowSeatLayoutHelper.applyParabolicCurve({
      seats: seatsUpdated,
      curve: selectedItem.seatsCurve || 0,
      spacing: rowSeatSize + selectedItem.seatsSpacing * 2,
      mode: selectedItem.curveMode,
      direction,
    });

    if (refreshLabels) {
      this.refreshRowLabels({
        selectedItem,
        rowSeatSize,
        textLeft,
        textRight,
        labelOffset,
      });
    }

    this.recalcRowGroup(selectedItem);
  }

  /**
   * Recalcula los límites del grupo de la fila tras cambiar sus asientos,
   * manteniendo la fila ESTÁTICA (el primer asiento no se mueve del canvas).
   *
   * addWithUpdate() (_calcBounds + _updateObjectsCoords) es NECESARIO para que
   * el asiento nuevo se re-normalice contra el bounding box actualizado; sin él
   * el asiento queda fuera de la línea. Pero _calcBounds calcula un bbox
   * alineado a los ejes y, al recentrar el grupo, desplaza toda la fila.
   *
   * applyParabolicCurve ancla la línea en seats[0] (coords locales), así que
   * seats[0] es el punto que debe permanecer fijo. Capturamos su posición
   * ABSOLUTA de canvas antes de recalcular y, después, trasladamos el grupo la
   * diferencia para devolver seats[0] a esa misma posición. Así la fila no se
   * mueve y solo crece/encoge por el extremo opuesto, con o sin rotación.
   */
  static recalcRowGroup(selectedItem: any) {
    const anchorBefore = this.getAnchorCanvasPoint(selectedItem);

    selectedItem.addWithUpdate();
    selectedItem.setCoords();

    const anchorAfter = this.getAnchorCanvasPoint(selectedItem);

    if (anchorBefore && anchorAfter) {
      const dx = anchorBefore.x - anchorAfter.x;
      const dy = anchorBefore.y - anchorAfter.y;

      if (dx !== 0 || dy !== 0) {
        selectedItem.set({
          left: selectedItem.left + dx,
          top: selectedItem.top + dy,
        });
        selectedItem.setCoords();
      }
    }
  }

  /**
   * Devuelve la posición ABSOLUTA de canvas del centro del primer asiento
   * (el ancla de la fila), proyectando su centro local a través de la matriz
   * de transformación del grupo (incluye posición, rotación y escala).
   */
  private static getAnchorCanvasPoint(selectedItem: any) {
    const seats = selectedItem.getObjects(ObjectTypesEnum.Group);
    if (!seats.length) return null;

    const anchor = seats[0];
    const localCenter = anchor.getCenterPoint();
    return fabric.util.transformPoint(
      localCenter,
      selectedItem.calcTransformMatrix()
    );
  }

  private static async addMissingSeats({
    selectedItem,
    value,
    seats,
    rowSeatSize,
    editable,
    container,
    objectOption,
  }: any) {
    const remainItems = value - seats.length;
    const category = selectedItem.category;

    for (let index = 0; index < remainItems; index++) {
      const seatsUpdated = selectedItem.getObjects(ObjectTypesEnum.Group);
      const nextPosition = this.getNextSeatPosition({
        seats: seatsUpdated,
        rowSeatSize,
        seatsSpacing: selectedItem.seatsSpacing,
      });

      if (!nextPosition) break;

      // nextPosition ya es el CENTRO deseado del asiento nuevo; SeatFactory
      // coloca el círculo en left/top = ese centro.
      const x = nextPosition.left;
      const y = nextPosition.top;

      const seat = await SeatFactory.create({
        left: x,
        top: y,
        rowSeatSize,
        defaultSeatColor: category.color ?? '#000000',
        editable,
        isPreview: false,
        container,
        objectOption,
        iconSvgString: iconCheck,
        category,
        sectionLabel: selectedItem.section.label, 
        rowLabel: selectedItem.row.label
      });

      selectedItem.insertAt(
        seat,
        selectedItem.getObjects(ObjectTypesEnum.Group).length,
        false
      );
    }
  }

  private static removeExtraSeats({ selectedItem, value, seats }: any) {
    const objectsToRemove = seats.slice(value);

    objectsToRemove.forEach((object: any) => {
      selectedItem.removeWithUpdate(object);
    });
  }

  private static applyDistribution({
    selectedItem,
    seats,
    setSeatValue,
  }: any) {
    if (selectedItem.seat.list.length <= 0) return;

    RowSeatLabelHelper.applySeatDistribution({
      selectedItem,
      seats,
      distributionKey: selectedItem.seat.key,
      startsAt: selectedItem.seat.startsAt,
      reverse: selectedItem.seat.reverse,
      setSeatValue,
      distributionObject: 'seat',
    });
  }

  static getNextSeatPosition({
    seats,
    rowSeatSize,
    seatsSpacing,
  }: {
    seats: any[];
    rowSeatSize: number;
    seatsSpacing: number;
  }) {
    if (!seats?.length) return null;

    const step = rowSeatSize + seatsSpacing * 2;

    const last = seats[seats.length - 1];

    // Centro REAL del último asiento (getCenterPoint refleja la rotación del
    // grupo). Devolvemos el CENTRO deseado del asiento nuevo; SeatFactory.create
    // coloca el círculo en ese punto (left/top = centro del círculo).
    const lastCenter = last.getCenterPoint();

    // Con un solo asiento no hay pendiente; extendemos en X (applyParabolicCurve
    // realineará según la dirección real cuando haya 2+ asientos).
    if (seats.length === 1) {
      return {
        left: lastCenter.x + step,
        top: lastCenter.y,
      };
    }

    const firstCenter = seats[0].getCenterPoint();

    const dx = lastCenter.x - firstCenter.x;
    const dy = lastCenter.y - firstCenter.y;

    const length = Math.hypot(dx, dy);

    if (!length) {
      return {
        left: lastCenter.x + step,
        top: lastCenter.y,
      };
    }

    // Pendiente calculada con centros reales (línea visual actual).
    const ux = dx / length;
    const uy = dy / length;

    return {
      left: lastCenter.x + ux * step,
      top: lastCenter.y + uy * step,
    };
  }

  static updateSeatsSpacing({
    selectedItem,
    value,
    seats,
    rowSeatSize,
    textRight,
    textLeft,
    labelOffset,
  }: any) {
    let spacing = Number(value) || 0;

    if (spacing < 0) spacing = 0;
    if (spacing % 1 !== 0) spacing = Math.trunc(spacing);

    selectedItem.set({
      seatsSpacing: spacing,
    });

    RowSeatLayoutHelper.applyParabolicCurve({
      seats,
      curve: selectedItem.seatsCurve || 0,
      spacing: rowSeatSize + spacing * 2,
      mode: selectedItem.curveMode,
    });

    this.refreshRowLabels({
      selectedItem,
      rowSeatSize,
      textLeft,
      textRight,
      labelOffset,
    });

    this.recalcRowGroup(selectedItem);
  }

  static updateSeatsCurve({
    selectedItem,
    value,
    seats,
    rowSeatSize,
    textRight,
    textLeft,
    labelOffset,
  }: any) {
    if (!seats || seats.length < 2) return;

    const curve = RowSeatLayoutHelper.normalizeCurveValue(value);

    // Editing the curve opts this row into the new length-relative calculation.
    // Rows never edited keep their stored geometry and legacy mode.
    selectedItem.set({
      seatsCurve: curve,
      curveMode: CURVE_MODE_RELATIVE,
    });

    RowSeatLayoutHelper.applyParabolicCurve({
      seats,
      curve,
      spacing: rowSeatSize + selectedItem.seatsSpacing * 2,
      mode: CURVE_MODE_RELATIVE,
    });

    this.refreshRowLabels({
      selectedItem,
      rowSeatSize,
      textLeft,
      textRight,
      labelOffset,
    });

    this.recalcRowGroup(selectedItem);
  }

  /**
   * Misma regla que DrawingHandler.shouldFlipRowText: una fila girada entre 90
   * y 270 grados muestra su etiqueta volteada 180° para que se lea derecha.
   */
  static shouldFlipRowText(angle: number) {
    return RowLabelHelper.shouldFlipRowText(angle);
  }

  static refreshRowLabels({
    selectedItem,
    rowSeatSize,
    textLeft,
    textRight,
    labelOffset,
  }: any) {
    const visibleText =
      selectedItem.row.displayedLabel !== ''
        ? selectedItem.row.displayedLabel
        : selectedItem.row.label !== ''
          ? selectedItem.row.label
          : '?';

    // La etiqueta de fila es hija del grupo, así que su orientación VISUAL es
    // group.angle + label.angle. Queremos que quede alineada con la fila y
    // legible, es decir, con orientación visual = angleHelp (+180 si el rango
    // obliga a voltear). Por eso el angle LOCAL de la etiqueta debe descontar la
    // rotación real del grupo. Esto funciona tanto en filas nuevas (group.angle
    // = 0, el giro vive en angleHelp) como en filas viejas (el giro vive en la
    // rotación real del grupo).
    const angleHelp = selectedItem.angleHelp || 0;
    const targetVisualAngle = RowLabelHelper.getReadableAngle(angleHelp);
    const groupAngle = selectedItem.angle || 0;
    const labelAngle = targetVisualAngle - groupAngle;

    if (textLeft?.length) {
      RowLabelHelper.updateExistingRowLabel({
        selectedItem,
        label: textLeft[0],
        rowSeatSize,
        text: visibleText,
        side: DirectionEnum.Left,
        labelOffset,
        angle: labelAngle,
      });
      textLeft[0].isRowTextFlipped = this.shouldFlipRowText(angleHelp);
    }

    if (textRight?.length) {
      RowLabelHelper.updateExistingRowLabel({
        selectedItem,
        label: textRight[0],
        rowSeatSize,
        text: visibleText,
        side: DirectionEnum.Right,
        labelOffset,
        angle: labelAngle,
      });
      textRight[0].isRowTextFlipped = this.shouldFlipRowText(angleHelp);
    }
  }
}
